/**
 * 图片组的聚合、加入、合并与取出（多对象联动的原子图操作）。
 *
 * aggregateSelectedImageNodes：把多个图片结果节点收进一个新建图片组——
 * 组放在选中节点包围盒中心，槽位按各节点带入（jobId / 原始引用 / 状态），
 * 被聚合图片的所有连线端点替换为组（上游生图 → 组；下游引用 ← 组，
 * 图片组作为上游本就展开为参考图，下游语义保持），原图片节点移除。
 *
 * addImageToGroup：单张图片节点拖线到已有组 → 加入该组（图片被吸收，
 * 连线同规则转接到组）；组内有生成中任务或图片生成中时拒绝，避免任务
 * 回写目标节点丢失。
 *
 * mergeImageGroups：图片组拖线到另一组 → 源组合并进目标组后消失，
 * 源组的血缘/参考边全部转接到目标组（去重）；任一组生成中时拒绝。
 *
 * extractImageFromGroup：把组内某槽位取出为独立图片节点（存原始引用，
 * 持久化纪律同"复制为节点"）；若存在专属上游生图节点（lastJobIds 恰好
 * 只含该槽位 jobId，智能体工作卡形态），一并带出：删其 → 组的边、
 * 改连 → 新图片节点；共享上游（普通批量生图一个节点管 N 张）不动组连线。
 * 取空后组一并移除，不留空壳。
 *
 * 连线端点替换逻辑共用 buildAbsorbRewire；全部经 boardActions.applyGraphPatch
 * 单步提交（单条撤销历史）。
 */
import { boardActions, getBoardState } from '../state/boardStore';
import { makeNode, nid } from '../utils';
import { DEFAULT_NODE_SIZES } from '../constants';
import { remoteizeBlobUrl } from '../imageStorage';
import { isAgentResultGroup } from './agentUnit';
import toast from '../../../components/Toast';
import type {
  CanvasEdge,
  CanvasNode,
  GenerateImageNodeData,
  ImageGroupNodeData,
  ImageNodeData,
} from '../types';

/** 组内单张状态收敛：processing/pending 视为 processing，其余视为 completed */
function slotStatusOf(status?: string | null): string {
  return status === 'processing' || status === 'pending' ? 'processing' : 'completed';
}

/** 整组状态：全部完成才算 completed（与聚合/批量生图的收敛口径一致） */
function groupStatusOf(statuses: string[]): string {
  return statuses.every((s) => s === 'completed') ? 'completed' : 'processing';
}

/**
 * 被吸收节点（removedIds）的所有连线端点替换为 absorbIntoId：
 * 两端都被吸收则丢弃，替换后按 source->target 去重。
 */
function buildAbsorbRewire(
  edges: CanvasEdge[],
  removedIds: Set<string>,
  absorbIntoId: string
): CanvasEdge[] {
  const touched = edges.filter(
    (e) => removedIds.has(e.sourceNodeId) || removedIds.has(e.targetNodeId)
  );
  const seen = new Set<string>();
  const rewired: CanvasEdge[] = [];
  for (const e of touched) {
    const source = removedIds.has(e.sourceNodeId) ? absorbIntoId : e.sourceNodeId;
    const target = removedIds.has(e.targetNodeId) ? absorbIntoId : e.targetNodeId;
    if (source === target) continue;
    const key = `${source}->${target}`;
    if (seen.has(key)) continue;
    seen.add(key);
    rewired.push({
      id: nid(),
      sourceNodeId: source,
      targetNodeId: target,
      sourceHandle: 'right',
      targetHandle: 'left',
    });
  }
  return rewired;
}

export async function aggregateSelectedImageNodes(nodeIds: string[]): Promise<void> {
  const state = getBoardState();
  const idSet = new Set(nodeIds);
  const images = state.nodes.filter(
    (n): n is CanvasNode<ImageNodeData> => n.type === 'image' && idSet.has(n.id)
  );
  if (images.length < 2) {
    toast.error('请至少选择 2 个图片节点再聚合');
    return;
  }

  // 吸收前把 blob 预览远程化：原节点会被删除，上传完成的回写将无处可去，
  // 组槽位若存进 blob 地址刷新后即死链且不可恢复。失败（blob 失效/上传失败）则阻止本次聚合
  const remoteized = await Promise.all(
    images.map((n) => remoteizeBlobUrl(n.data.imageUrl || ''))
  );
  const failedIdx = remoteized.findIndex((u) => !u);
  if (failedIdx >= 0) {
    toast.error(`第 ${failedIdx + 1} 张图片尚未上传成功（或本会话已失效），稍后再聚合`);
    return;
  }

  // 上传期间（秒级网络操作）图可能已被并发编辑：节点与连线一律以最新状态为准，
  // 否则过期快照会把窗口期内新增的连线丢失、已删除的连线复活
  const fresh = getBoardState();
  const remoteById = new Map(images.map((n, i) => [n.id, remoteized[i] as string]));
  const stillImages = fresh.nodes.filter(
    (n): n is CanvasNode<ImageNodeData> => n.type === 'image' && idSet.has(n.id)
  );
  if (stillImages.length < 2) {
    toast.error('选中的图片节点已变化，请重新选择后再聚合');
    return;
  }

  // 组放选中节点包围盒中心（按默认尺寸折算）
  const minX = Math.min(...stillImages.map((n) => n.x));
  const minY = Math.min(...stillImages.map((n) => n.y));
  const maxX = Math.max(...stillImages.map((n) => n.x + n.width));
  const maxY = Math.max(...stillImages.map((n) => n.y + n.height));
  const { width: gw, height: gh } = DEFAULT_NODE_SIZES['image_group'];
  const maxZ = Math.max(...stillImages.map((n) => n.zIndex));

  const statuses = stillImages.map(() => 'completed');
  const groupNode = makeNode<ImageGroupNodeData>(
    'image_group',
    { x: (minX + maxX) / 2 - gw / 2, y: (minY + maxY) / 2 - gh / 2 },
    {
      jobIds: stillImages.map((n) => n.data.jobId ?? null),
      imageUrls: stillImages.map((n) => remoteById.get(n.id)!),
      statuses,
      status: groupStatusOf(statuses),
    },
    undefined,
    maxZ
  );

  boardActions.applyGraphPatch({
    addNodes: [groupNode],
    removeNodeIds: [...idSet],
    addEdges: buildAbsorbRewire(fresh.edges, idSet, groupNode.id),
  });
  toast.success(`已聚合 ${stillImages.length} 张图片为图片组`);
}

/** 单张图片节点加入已有图片组：图片被吸收为组末尾槽位，连线转接到组 */
export async function addImageToGroup(imageNodeId: string, groupNodeId: string): Promise<void> {
  const state = getBoardState();
  const image = state.nodes.find((n) => n.id === imageNodeId);
  const group = state.nodes.find((n) => n.id === groupNodeId);
  if (!image || image.type !== 'image' || !group || group.type !== 'image_group') return;

  if (slotStatusOf((image.data as ImageNodeData).status) === 'processing') {
    toast.error('图片还在生成中，完成后再加入图片组');
    return;
  }
  const gd = group.data as ImageGroupNodeData;
  if ((gd.statuses ?? []).some((s) => s === 'processing')) {
    toast.error('图片组内还有生成中的任务，完成后再加入');
    return;
  }

  // 吸收前把 blob 预览远程化（原节点会被删除，回写将无处可去；组槽位存 blob 刷新即死链）
  const remoteized = await remoteizeBlobUrl((image.data as ImageNodeData).imageUrl || '');
  if (!remoteized) {
    toast.error('该图片尚未上传成功（或本会话已失效），稍后再加入图片组');
    return;
  }

  // 上传期间图可能已被并发编辑：节点、组数据与连线重读最新状态，
  // 避免用过期组数据全量覆盖窗口期内完成回写的槽位
  const fresh = getBoardState();
  const freshImage = fresh.nodes.find((n) => n.id === imageNodeId);
  const freshGroup = fresh.nodes.find((n) => n.id === groupNodeId);
  if (!freshImage || freshImage.type !== 'image' || !freshGroup || freshGroup.type !== 'image_group') {
    return;
  }
  const fgd = freshGroup.data as ImageGroupNodeData;
  if ((fgd.statuses ?? []).some((s) => s === 'processing')) {
    toast.error('图片组内还有生成中的任务，完成后再加入');
    return;
  }

  const statuses = [...(fgd.statuses ?? []), 'completed'];
  boardActions.applyGraphPatch({
    removeNodeIds: [imageNodeId],
    addEdges: buildAbsorbRewire(fresh.edges, new Set([imageNodeId]), groupNodeId),
    nodeDataPatches: [
      {
        id: groupNodeId,
        data: {
          jobIds: [...(fgd.jobIds ?? []), (freshImage.data as ImageNodeData).jobId ?? null],
          imageUrls: [...(fgd.imageUrls ?? []), remoteized],
          statuses,
          status: groupStatusOf(statuses),
        },
      },
    ],
  });
  toast.success('已加入图片组');
}

/** 图片组合并：源组各槽位按序追加到目标组尾部，源组消失，连线转接去重 */
export function mergeImageGroups(sourceGroupId: string, targetGroupId: string): void {
  const state = getBoardState();
  const source = state.nodes.find((n) => n.id === sourceGroupId);
  const target = state.nodes.find((n) => n.id === targetGroupId);
  if (!source || source.type !== 'image_group' || !target || target.type !== 'image_group') return;
  if (sourceGroupId === targetGroupId) return;

  const sd = source.data as ImageGroupNodeData;
  const td = target.data as ImageGroupNodeData;
  if ((sd.statuses ?? []).some((s) => s === 'processing') || (td.statuses ?? []).some((s) => s === 'processing')) {
    toast.error('组内还有生成中的任务，完成后再合并');
    return;
  }

  const statuses = [...(td.statuses ?? []), ...(sd.statuses ?? [])];
  boardActions.applyGraphPatch({
    removeNodeIds: [sourceGroupId],
    addEdges: buildAbsorbRewire(state.edges, new Set([sourceGroupId]), targetGroupId),
    nodeDataPatches: [
      {
        id: targetGroupId,
        data: {
          jobIds: [...(td.jobIds ?? []), ...(sd.jobIds ?? [])],
          imageUrls: [...(td.imageUrls ?? []), ...(sd.imageUrls ?? [])],
          statuses,
          status: groupStatusOf(statuses),
          // 目标组主图保持不变；目标无主图时沿用源组主图（目标槽位在前，引用不需重映射）
          ...(td.primaryJobId ? {} : sd.primaryJobId ? { primaryJobId: sd.primaryJobId } : {}),
        },
      },
    ],
  });
  toast.success(`已合并图片组（共 ${(td.imageUrls ?? []).length + (sd.imageUrls ?? []).length} 张）`);
}

/** 组内移除某槽位后的数据补丁（含主图重映射；与 ImageGroupNode.removeImage 同规则） */
function buildGroupRemovalPatch(
  data: ImageGroupNodeData,
  index: number
): Record<string, unknown> {
  const jobIds = (data.jobIds ?? []).filter((_, i) => i !== index);
  const imageUrls = (data.imageUrls ?? []).filter((_, i) => i !== index);
  const statuses = (data.statuses ?? []).filter((_, i) => i !== index);
  const removedJobId = (data.jobIds ?? [])[index];
  const patch: Record<string, unknown> = { jobIds, imageUrls, statuses };
  if (removedJobId && data.primaryJobId === removedJobId) {
    patch.primaryJobId = jobIds[0] ?? null;
  } else if (typeof data.primaryJobId === 'string' && data.primaryJobId.startsWith('#idx:')) {
    const oldIdx = Number(data.primaryJobId.slice('#idx:'.length));
    if (oldIdx === index) {
      patch.primaryJobId = imageUrls.length > 0 ? '#idx:0' : null;
    } else if (oldIdx > index) {
      patch.primaryJobId = `#idx:${oldIdx - 1}`;
    }
  }
  return patch;
}

export function extractImageFromGroup(groupNodeId: string, index: number): void {
  const state = getBoardState();
  const groupNode = state.nodes.find((n) => n.id === groupNodeId);
  if (!groupNode || groupNode.type !== 'image_group') return;
  const data = groupNode.data as ImageGroupNodeData;

  const rawUrl = (data.imageUrls ?? [])[index] ?? null;
  const jobId = (data.jobIds ?? [])[index] ?? null;
  if (!rawUrl && !jobId) {
    toast.error('该槽位还没有可取出的图片');
    return;
  }

  // 新图片节点放组右侧（与"复制为节点"同款摆放惯例），存原始引用而非展示 URL
  const imageNode = makeNode<ImageNodeData>(
    'image',
    { x: groupNode.x + groupNode.width + 60, y: groupNode.y + index * 24 },
    {
      jobId,
      imageUrl: rawUrl,
      status: 'completed',
    },
    undefined,
    groupNode.zIndex
  );

  // 专属上游判定：连到组的生图节点里，lastJobIds 恰好只含本槽 jobId 的那个
  const addEdges: CanvasEdge[] = [];
  let removeEdgeIds: string[] = [];
  if (jobId) {
    for (const e of state.edges.filter((x) => x.targetNodeId === groupNodeId)) {
      const src = state.nodes.find((n) => n.id === e.sourceNodeId);
      if (!src || src.type !== 'generate_image') continue;
      const lastJobIds = (src.data as GenerateImageNodeData).lastJobIds ?? [];
      if (lastJobIds.length === 1 && lastJobIds[0] === jobId) {
        removeEdgeIds = [e.id];
        addEdges.push({
          id: nid(),
          sourceNodeId: src.id,
          targetNodeId: imageNode.id,
          sourceHandle: 'right',
          targetHandle: 'left',
        });
        break;
      }
    }
  }

  // 取空最后一个槽位时组一并移除，不留空壳（其余组连线随节点清理）；
  // 智能体批次的结果组除外：冻结单元不允许被取空，只能整批删除
  const removalPatch = buildGroupRemovalPatch(data, index);
  const groupEmpty =
    ((removalPatch.jobIds as (string | null)[]).length === 0 &&
      (removalPatch.imageUrls as (string | null)[]).length === 0);
  if (groupEmpty && isAgentResultGroup(groupNodeId, state.nodes)) {
    toast.error('该图片组是智能体批次的结果组，不能取空；如需删除请整体删除该批次');
    return;
  }

  boardActions.applyGraphPatch({
    addNodes: [imageNode],
    addEdges,
    removeEdgeIds,
    ...(groupEmpty
      ? { removeNodeIds: [groupNodeId] }
      : { nodeDataPatches: [{ id: groupNodeId, data: removalPatch }] }),
  });
  toast.success(groupEmpty ? '已取出为独立图片节点（组已空，图片组一并移除）' : '已取出为独立图片节点');
}
